import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { UserPlus, KeyRound, Trash2, ShieldCheck } from "lucide-react";
import {
  Card, Button, SectionLabel, StatusPill, NeutralTag,
} from "./theme";

const inputStyle = {
  borderColor: "var(--th-border-strong)",
  borderRadius: 8,
  backgroundColor: "var(--th-surface)",
};

function CreateDialog({ open, onOpenChange, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [saving, setSaving] = useState(false);

  const genPwd = () => {
    const chars = "abcdefghjkmnpqrstuvwxyz23456789";
    let pw = "";
    for (let i = 0; i < 10; i++) pw += chars[Math.floor(Math.random() * chars.length)];
    setPassword(pw);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/users", { email: email.trim().toLowerCase(), password, name: name.trim(), role });
      toast.success(`Utilisateur ${r.data.email} créé`);
      setEmail(""); setName(""); setPassword(""); setRole("user");
      onCreated();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ borderRadius: 12 }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "var(--th-font-sans)", fontWeight: 500, color: "var(--th-text)" }}>
            Nouvel utilisateur
          </DialogTitle>
          <DialogDescription style={{ color: "var(--th-text-2)" }}>
            Créez un accès pour un testeur. Il pourra se connecter immédiatement avec ces identifiants.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-email" style={{ color: "var(--th-text)" }}>Email</Label>
            <Input id="new-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} data-testid="user-new-email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-name" style={{ color: "var(--th-text)" }}>Nom affiché (optionnel)</Label>
            <Input id="new-name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} data-testid="user-new-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-pwd" style={{ color: "var(--th-text)" }}>Mot de passe (min 6 caractères)</Label>
            <div className="flex gap-2">
              <Input
                id="new-pwd" value={password} onChange={(e) => setPassword(e.target.value)}
                required minLength={6} style={inputStyle} data-testid="user-new-password"
              />
              <Button type="button" variant="secondary" onClick={genPwd}>Générer</Button>
            </div>
            <div className="text-xs" style={{ color: "var(--th-text-3)" }}>
              Copiez ce mot de passe — il ne s&apos;affichera plus après création.
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-role" style={{ color: "var(--th-text)" }}>Rôle</Label>
            <select
              id="new-role" value={role} onChange={(e) => setRole(e.target.value)}
              className="w-full h-10 px-3 text-sm" style={inputStyle} data-testid="user-new-role"
            >
              <option value="user">Utilisateur (accès normal)</option>
              <option value="admin">Administrateur (peut gérer les utilisateurs)</option>
            </select>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" variant="primary" disabled={saving} data-testid="user-new-submit">
              {saving ? "Création…" : "Créer l'accès"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPwdDialog({ user, onClose, onDone }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => setPwd(""), [user?.user_id]);
  if (!user) return null;

  const genPwd = () => {
    const chars = "abcdefghjkmnpqrstuvwxyz23456789";
    let p = ""; for (let i = 0; i < 10; i++) p += chars[Math.floor(Math.random() * chars.length)];
    setPwd(p);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/users/${user.user_id}/reset-password`, { password: pwd });
      toast.success("Mot de passe réinitialisé");
      onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent style={{ borderRadius: 12 }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "var(--th-font-sans)", fontWeight: 500, color: "var(--th-text)" }}>
            Réinitialiser le mot de passe
          </DialogTitle>
          <DialogDescription style={{ color: "var(--th-text-2)" }}>
            Nouveau mot de passe pour <strong>{user.email}</strong>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={pwd} onChange={(e) => setPwd(e.target.value)}
              required minLength={6}
              placeholder="Nouveau mot de passe (min 6)"
              style={inputStyle}
              data-testid="user-reset-pwd-input"
            />
            <Button type="button" variant="secondary" onClick={genPwd}>Générer</Button>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>Annuler</Button>
            <Button type="submit" variant="primary" disabled={saving} data-testid="user-reset-pwd-submit">
              {saving ? "…" : "Réinitialiser"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersSection({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [resetUser, setResetUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data.users);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (u, active) => {
    try {
      await api.patch(`/users/${u.user_id}`, { is_active: active });
      toast.success(active ? "Compte activé" : "Compte désactivé");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer définitivement l'accès de ${u.email} ?`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="users-section">
      <div className="flex items-center justify-between">
        <div className="text-sm" style={{ color: "var(--th-text-2)" }}>
          {users.length} utilisateur{users.length > 1 ? "s" : ""} · vous pouvez créer jusqu&apos;à autant de comptes que nécessaire.
        </div>
        <Button variant="primary" onClick={() => setOpenCreate(true)} data-testid="user-create-btn">
          <UserPlus size={14} /> Créer un accès
        </Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="th-table w-full">
          <thead>
            <tr>
              <th className="text-left">Email</th>
              <th className="text-left">Nom</th>
              <th className="text-left">Rôle</th>
              <th className="text-center">Actif</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-6 text-center" style={{ color: "var(--th-text-3)" }}>Chargement…</td></tr>
            )}
            {!loading && users.map((u) => {
              const isMe = u.user_id === currentUserId;
              return (
                <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{u.email}</div>
                    {isMe && <div className="text-xs" style={{ color: "var(--th-text-3)" }}>(vous)</div>}
                  </td>
                  <td style={{ color: "var(--th-text-2)" }}>{u.name || "—"}</td>
                  <td>
                    {u.role === "admin"
                      ? <StatusPill level="MOYEN"><ShieldCheck size={11} style={{ marginRight: 2 }} /> Admin</StatusPill>
                      : <NeutralTag>Utilisateur</NeutralTag>}
                  </td>
                  <td className="text-center">
                    <Switch
                      checked={u.is_active !== false}
                      onCheckedChange={(v) => toggleActive(u, v)}
                      disabled={isMe}
                      data-testid={`user-toggle-${u.user_id}`}
                    />
                  </td>
                  <td className="text-right space-x-2 whitespace-nowrap">
                    <button
                      className="th-btn th-btn--ghost h-8 px-3 text-xs"
                      onClick={() => setResetUser(u)}
                      data-testid={`user-reset-${u.user_id}`}
                    >
                      <KeyRound size={12} /> Mdp
                    </button>
                    {!isMe && (
                      <button
                        className="th-btn th-btn--ghost h-8 px-3 text-xs"
                        style={{ color: "var(--th-haut-text)" }}
                        onClick={() => remove(u)}
                        data-testid={`user-delete-${u.user_id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && users.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-sm" style={{ color: "var(--th-text-3)" }}>Aucun utilisateur.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <CreateDialog open={openCreate} onOpenChange={setOpenCreate} onCreated={load} />
      <ResetPwdDialog user={resetUser} onClose={() => setResetUser(null)} onDone={load} />
    </div>
  );
}
